import { BRANCHES, LAND, STRUCTURES } from '@somnhold/content';
import type { StructureInit } from '../battle/create.ts';
import { cellId } from '../grid.ts';
import { hexToWorld, type Hex } from '../hex.ts';
import { openFissures } from '../land.ts';
import { idiv, mulBp, withinRange } from '../math/fixed.ts';
import { deriveSeed } from '../math/rng.ts';
import type { MapDef } from '../types.ts';

/*
 * Where the general cracks the field open (docs/CONSTRUCCION.md §6). Every wave it draws a few sets of fissures
 * nobody can foresee and keeps the one whose ways in meet the bastion where it is weakest: the least fire from
 * the towers, the thinnest wall, the shortest walk. It never says where beforehand.
 */

/** Fire per second the towers pour on a cell of terraced land (each reaching it, by its level and the height it stands on). */
export const coverAt = (map: MapDef, towers: StructureInit[], h: Hex): number => {
  const land = map.land;
  const R = map.radius;
  const at = hexToWorld(h.q, h.r);
  let fire = 0;
  for (const t of towers) {
    // A tower, or one mounted on a wall run (the adarve), with what its branch adds (docs/OBRAS.md).
    const def = STRUCTURES[t.mount?.type ?? t.type];
    const level = t.mount?.level ?? t.level;
    if (def.category !== 'tower' || def.reload <= 0 || (t.hp !== undefined && t.hp <= 0)) continue;
    const br = t.branch ? BRANCHES[t.branch] : null;
    const c = hexToWorld(t.q, t.r);
    const range = def.range + (br?.rangeAdd ?? 0) + (t.mount ? LAND.levelRange : 0) + (land ? (land.level[cellId(R, t.q, t.r)] ?? 0) * LAND.levelRange : 0);
    if (!withinRange(c.x, c.y, at.x, at.y, range)) continue;
    const dmg = mulBp((def.damage[level - 1] ?? 0) * (br?.shots ?? 1), br?.damageBp ?? 10000);
    fire += idiv(dmg * 12, mulBp(def.reload, br?.reloadBp ?? 10000));
  }
  return fire;
};

/**
 * How much a fissure's way in costs the nightmares: the fire on its door and the cell before it, the wall to
 * break there, and the walk. Lower is weaker.
 */
const wayCost = (map: MapDef, towers: StructureInit[], walls: Map<string, number>, sector: number): number => {
  const sec = map.sectors[sector];
  if (!sec) return 0;
  const before = sec.path[sec.path.length - 1] ?? sec.gate;
  const fire = coverAt(map, towers, sec.gate) + coverAt(map, towers, before);
  const wall = walls.get(`${sec.gate.q},${sec.gate.r}`) ?? 0;
  return fire * 40 + idiv(wall, 4) + sec.path.length * 6;
};

/**
 * The wave's map on terraced land: of `LAND.riftDraws` sets of fissures drawn from the wave's seed, the one
 * whose ways in cost least (the first on a tie).
 */
export const weakestRifts = (map: MapDef, structures: StructureInit[], seed: number): MapDef => {
  const blocked = structures.filter((x) => STRUCTURES[x.type].blocks).flatMap((x) => [x, ...(x.foot ?? [])]);
  const towers = structures.filter((x) => STRUCTURES[x.type].category === 'tower' || !!x.mount);
  const walls = new Map<string, number>();
  for (const x of structures) if (STRUCTURES[x.type].blocks) for (const h of [x, ...(x.foot ?? [])]) walls.set(`${h.q},${h.r}`, x.hp ?? STRUCTURES[x.type].hp);
  // A fissure keeps its distance from what stands, but not from traps: a cheap row of spikes can't choose where the field cracks.
  const standing = structures.filter((x) => STRUCTURES[x.type].category !== 'trap');
  let best: MapDef | null = null;
  let bestCost = 0;
  for (let k = 0; k < LAND.riftDraws; k++) {
    const m = openFissures(map, standing, blocked, k === 0 ? seed : deriveSeed(seed, 500 + k));
    let cost = 0;
    for (let i = 0; i < m.sectors.length; i++) cost += wayCost(m, towers, walls, i);
    if (!best || cost < bestCost) {
      best = m;
      bestCost = cost;
    }
  }
  return best ?? map;
};
